'use client'

import { css } from '@lastbrand/web-ui-styled-system/css'
import { useCallback, type ReactNode } from 'react'

import { ProductReviewCardDs, ProductReviewCardDsFooter } from '@src/components/blocks/product-review-card-ds'
import { RichTextDS } from '@src/components/blocks/rich-text-ds'
import { SvdProductCard, mapSvdProduct } from '@src/components/svd-product-card'
import { isPlSvdCard } from '@src/components/svd-product-card/map-pl-svd-card'
import { TEST_LOCATORS } from '@src/constants/test-locators'
import { Carousel, CarouselContent, CarouselItem } from '@src/ds/blocks/ds-carousel'
import type GenericContent from '@src/quince-site-common/src/domains/GenericContent'
import RichTextDomain from '@src/quince-site-common/src/domains/RichText'
import { useBatchedAnalyticsForProductCard } from '@src/quince-site-common/src/hooks/useBatchedAnalyticsForProductCard'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { useTrackIsElementVisible } from '@src/quince-site-common/src/hooks/useTrackIsElementVisible'
import { SectionProps } from '@src/SectionRenderer/SectionRenderer.types'
import {
  getContentContainerTrackingMetadata,
  getTrackingContext,
} from '@src/SectionRenderer/sections/productListSection/productListSection.helpers'
import { getFindingMethodAggregate } from '@src/telemetry/findingMethod'
import type { ISvdCard } from '@src/types/svd-product-card.types'
import { shouldUseSvdCard } from '@src/utils/should-use-svd-card'

import type { Document, Node } from '@contentful/rich-text-types'

export const COLUMNS = 6

// Flatten a Contentful rich-text document to its plain text, so the header's
// test id can be keyed off the actual heading copy (mirrors faq-ds / back-to-school).
const plainTextFromNode = (node: Node): string => {
  if (node.nodeType === 'text' && 'value' in node) return (node as { value: string }).value
  if ('content' in node && Array.isArray((node as { content: Node[] }).content)) {
    return (node as { content: Node[] }).content.map(plainTextFromNode).join(' ')
  }
  return ''
}

export const plainTextFromDocument = (doc: Document | undefined | null): string =>
  doc && Array.isArray(doc.content)
    ? doc.content
        .map(n => plainTextFromNode(n as Node))
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim()
    : ''

// Outer band: full-viewport width — colored band / padding lives here so a
// 100% wrapper paints edge to edge on wide monitors. No max-width.
export const containerClass = css({
  width: '100%',
  py: { base: 'space-48', lg: 'space-64' },
  pr: { base: 0, lg: 'space-48' },
  pl: { base: 'space-16', lg: 'space-48' },
})

// Inner content: capped at 1504px and centered. Heading + grid/carousel live
// here. Matches the canonical "outer 100% / inner 1504" widget pattern (see
// `faq-ds.tsx`, `footer-widget-ds.tsx` `maxWidthContainerStyles`,
// `back-to-school-ds-widget.tsx` `sectionInnerClass`).
export const innerClass = css({
  display: 'flex',
  flexDirection: 'column',
  gap: { base: 'space-16', lg: 'space-24' },
  maxWidth: '1504px',
  width: '100%',
  marginInline: 'auto',
})

export const titleClass = css({
  textStyle: { base: 'title-14', lg: 'title-16' },
})

export const carouselColumnClass = css({
  width: 'full',
  containerType: 'inline-size',
  '& [data-slot="carousel-content"]': {
    gap: { base: 'space-16', lg: 'space-24' },
  },
  '& [data-slot="carousel-item"]': {
    minWidth: 0,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: {
      base: '56vw',
      lg: `calc((100cqi - ${COLUMNS - 1} * var(--web-ui-spacing-space-24)) / ${COLUMNS})`,
    },
  },
  // Breathing room at both ends of the full-bleed track (see `carouselViewportCss`), so the edge cards
  // still rest on the band's 16px gutter — lined up with the heading — while the track itself runs to
  // both screen edges mid-scroll. Margins on the edge slides, not padding on the track, because that is
  // literally what Embla measures: `SlideSizes` reads `startGap` as the container→first-slide distance
  // and `endGap` as the last slide's computed end margin, folds both into `contentSize`, and then pins
  // the first snap to `0` (`measureBounded`: `if (isFirst) return max`) — which is what keeps the leading
  // margin as the at-rest inset instead of scrolling it away. Reset at `lg`, where the band takes its
  // own padding back.
  '& [data-slot="carousel-item"]:first-child': { marginInlineStart: { base: 'space-16', lg: '0' } },
  '& [data-slot="carousel-item"]:last-child': { marginInlineEnd: { base: 'space-16', lg: '0' } },
})

// Full-bleed on BOTH sides below `lg`. The band already drops its RIGHT padding on mobile
// (`containerClass` pr `base: 0`), so only the left gutter was still clipping the row — but that `pl`
// has to stay, because it is what gives the HEADING its gutter. So the bleed moves the carousel
// VIEWPORT instead: pull it back over the band's `pl` and add that 16px to its width, and it spans
// exactly 100vw with the heading left untouched.
//
// The viewport is also the right element on the merits — it owns the `overflow: hidden`, so its edge is
// the clip the cards are actually cut off at. Merged by web-ui into the viewport's own `css()` call
// (`css({overflow, minWidth, width}, viewportCss)`), so this `width` deterministically beats the slot's
// default `width: 'full'`.
//
// `100cqi` (the `lg` card basis) resolves against `carouselColumnClass`, not the viewport, so widening
// the viewport cannot disturb the desktop-width formula. Still reset at `lg`: this carousel renders for
// any non-desktop device, and `useDeviceInfo` counts tablets as mobile, so a ≥1200px tablet would
// otherwise bleed against the band's `space-48` padding.
//
// EXPORTED because the PL widget (`components/widgets/honest-reviews`) reuses `containerClass` +
// `carouselColumnClass` and renders the same carousel: this and the edge-slide insets above are ONE
// unit, so a consumer that takes the insets without this bleed leaves its first card 32px from the
// screen edge (the band's gutter plus the inset) while its heading stays on 16px.
export const carouselViewportCss = css.raw({
  width: { base: 'calc(100% + var(--web-ui-spacing-space-16))', lg: 'full' },
  marginInlineStart: { base: 'calc(var(--web-ui-spacing-space-16) * -1)', lg: '0' },
})

export const CAROUSEL_OPTS = {
  align: 'start' as const,
  slidesToScroll: 1,
  dragFree: true,
}

export const gridClass = css({
  display: 'grid',
  gridTemplateColumns: `repeat(${COLUMNS}, 1fr)`,
  gap: { base: 'space-16', lg: 'space-24' },
})

export function HonestReviewsDs({
  sectionConfig: { id, richTexts = [], genericContents: reviews = [], trackingMetaData, tracker, sectionType },
}: SectionProps) {
  const headingRichTextProps = RichTextDomain.makeRichTextProps(richTexts[0])
  const products = reviews.slice(0, COLUMNS)

  const getIsVisibleTrackParams = useCallback(() => ({ ...trackingMetaData }), [trackingMetaData])
  const { ref: trackIsVisibleRef } = useTrackIsElementVisible(getIsVisibleTrackParams)

  // Legacy parity (HonestReviewsV2.tsx:84-85, STRFRNT-16872): legacy interpolates the tracker
  // unconditionally — an absent tracker still initializes the batch under "undefined_honest_reviews"
  // — so DS must too, or impressions on tracker-less instances are silently dropped.
  const productViewTracker = `${String(tracker)}_honest_reviews`
  useBatchedAnalyticsForProductCard({ productViewTracker, trackingMetaData })

  const contentContainerTrackingMetadata = getContentContainerTrackingMetadata(trackingMetaData)

  const { deviceType } = useDeviceInfo()
  const isDesktop = deviceType === DEVICE_TYPES.DESKTOP

  if (!products.length) return null

  const buildReviewCardProps = (review: GenericContent, idx: number) => {
    const product = review.products[0]
    if (!product) return null

    const trackingContext = getTrackingContext(tracker, product.slug)
    const productCard = { product }
    const { reviews: { averageRating = 0 } = {} } = product

    const wishlistTrackConfig = {
      ...trackingContext,
      sectionType,
      sectionid: id,
      ...trackingMetaData?.context,
      productPosition: idx,
      ...contentContainerTrackingMetadata,
      findingMethod: getFindingMethodAggregate(),
    }

    return {
      key: product.productId,
      // Kept alongside `productCard` so `renderReviewCard` can shape-check the raw node without
      // reaching back into `review`.
      product,
      productCard,
      rating: averageRating,
      // Route through `RichTextDomain.makeRichTextProps` (the canonical
      // accessor used for the heading at line 68 above). Reading `.raw`
      // directly is dangerous: in some SSR-data shapes the Contentful raw
      // field can arrive as a JSON string rather than a parsed `Document`,
      // and `RichTextDS` would silently render nothing. The domain accessor
      // is the single chokepoint where parsing/typing live.
      descriptionContent: RichTextDomain.makeRichTextProps(review.titleText)?.content,
      authorContent: RichTextDomain.makeRichTextProps(review.content)?.content,
      // Same click tracker as the legacy fork — the `tracker` query param feeds one analytics
      // series that must not split across the DS rollout (STRFRNT-16623).
      // @i18n-ignore
      tracker: tracker && `${tracker}__product_carousel`,
      productClickTrackConfig: {
        trackInternal: true,
        trackGA: true,
        ...trackingContext,
        context: {
          sectionType,
          sectionid: id,
          ...trackingMetaData?.context,
          productPosition: idx,
          ...contentContainerTrackingMetadata,
          findingMethod: getFindingMethodAggregate(),
        },
      },
      wishlistTrackConfig,
    }
  }

  const cardPropsList = products.flatMap((review, idx) => {
    const props = buildReviewCardProps(review, idx)
    return props ? [props] : []
  })

  /**
   * Shape-guarded card render, shared by the desktop grid and the mobile carousel so both forks stay
   * in step. `ProductReviewCardDs` builds its own tile out of legacy-only fields
   * (`flags.readyToShipEnabled`, `colorVariants`, `getImagesForOptions`), so an SVD card must never
   * reach it — route to `SvdProductCard` with the same review footer instead, mirroring the legacy
   * fork in `SectionRenderer/sections/honestReviewsV2/HonestReviewsV2.tsx`.
   *
   * The check is on the DATA, not just the flag: this section's product comes from
   * `genericContents[].products[0]`, a node the v4 BFF does not swap for an SVD card, so an
   * SVD-bucketed page still lands here legacy-shaped.
   */
  const renderReviewCard = ({ product, ...cardProps }: Omit<(typeof cardPropsList)[number], 'key'>): ReactNode => {
    if (shouldUseSvdCard() && isPlSvdCard(product)) {
      return (
        <SvdProductCard
          product={mapSvdProduct(product as unknown as ISvdCard)}
          hideTitle
          customFooter={
            <ProductReviewCardDsFooter
              rating={cardProps.rating}
              descriptionContent={cardProps.descriptionContent}
              authorContent={cardProps.authorContent}
            />
          }
          productViewTracker={productViewTracker}
          productClickTrackConfig={cardProps.productClickTrackConfig}
          wishlistTrackingConfig={cardProps.wishlistTrackConfig}
        />
      )
    }
    return <ProductReviewCardDs {...cardProps} />
  }

  return (
    <div ref={trackIsVisibleRef} id={id.toLowerCase()} className={containerClass} data-testid="honest-reviews-ds">
      <div className={innerClass}>
        {headingRichTextProps?.content && (
          <div data-testid={TEST_LOCATORS.HONEST_REVIEWS.HEADER(plainTextFromDocument(headingRichTextProps.content))}>
            <RichTextDS content={headingRichTextProps.content} className={titleClass} />
          </div>
        )}
        {isDesktop ? (
          <div className={gridClass}>
            {cardPropsList.map(({ key, ...cardProps }, index) => (
              <div key={key} data-testid={TEST_LOCATORS.HONEST_REVIEWS.PRODUCT(index)}>
                {renderReviewCard(cardProps)}
              </div>
            ))}
          </div>
        ) : (
          <Carousel opts={CAROUSEL_OPTS}>
            <div className={carouselColumnClass}>
              <CarouselContent viewportCss={carouselViewportCss}>
                {cardPropsList.map(({ key, ...cardProps }, index) => (
                  <CarouselItem key={key} data-testid={TEST_LOCATORS.HONEST_REVIEWS.PRODUCT(index)}>
                    {renderReviewCard(cardProps)}
                  </CarouselItem>
                ))}
              </CarouselContent>
            </div>
          </Carousel>
        )}
      </div>
    </div>
  )
}

export default HonestReviewsDs
